/**
 * The command palette (`070-search-and-links.md` §4).
 *
 * **This is the primary navigation surface of the application.** The sidebar tree is for
 * browsing; the palette is for *arriving*. It answers "who was that NPC again?" in under
 * five seconds, which is the reason this app exists (`000-overview.md`).
 *
 * The four properties that make it feel right, and the one line each that buys them:
 *
 *  - **Never blanks.** `placeholderData: keepPreviousData` in `useSearch` — the previous
 *    results stay on screen, dimmed, while the next ones are in flight. Flicker on every
 *    keystroke is the classic palette bug.
 *  - **Never stale.** TanStack's `signal` aborts the in-flight request on every keystroke,
 *    so a slow `ith` cannot land on top of a fast `ithkar`.
 *  - **Never lags.** 150 ms debounce on the SEARCH legs only. The mode switch is
 *    client-side and is not debounced — typing `>` must feel like flipping a switch.
 *  - **Never needs the mouse.** `cmdk` owns ↑/↓/Enter, and we do not fight it. Criterion 5
 *    is "zero mouse events end to end".
 *
 * Focus returns to whatever had it when the palette opened — including the caret's position in
 * the Tiptap document. This was long attributed to Radix's `FocusScope`; **it is not**, and
 * tabbing the app for M7 proved it (`Esc` left focus on `<body>`). It is now done explicitly —
 * see `restoreFocusTo` below. The palette is still a real dialog rather than a hand-rolled
 * overlay, for the focus TRAP and the `role="dialog"` that the rest of the keymap keys off.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';
import {
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  FilePlus2,
  FileText,
  Keyboard,
  ListTodo,
  Moon,
  Rows3,
  Settings as SettingsIcon,
  Sparkles,
  Star,
  StarOff,
  Tag as TagIcon,
  Trash2,
} from 'lucide-react';
import type { TreePage } from '@/api/endpoints/pages';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { SearchSnippet } from '@/components/SearchSnippet';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { ulid } from '@/lib/ulid';
import { cn } from '@/lib/utils';
import type { Theme } from '@/stores/theme';
// M7: the theme and density commands must PERSIST, not merely apply. `preferences.tsx` writes
// localStorage (the pre-paint read path) AND queues the `settings.patch` — calling the raw
// store setters here would leave the server out of date and a new browser inheriting nothing.
import { useDensityPreference, useThemePreference } from '@/features/settings/preferences';
import { parsePaletteInput } from '@/features/search/paletteMode';
import { useSearch } from '@/features/search/useSearch';
import { useTags } from '@/features/tags/useTags';
// The palette CREATES pages and toggles favorites, so it needs the page mutation layer.
// `111` §2 asks features not to import each other; the same document's folder map
// nonetheless puts `CommandPalette.tsx` in `features/search/`, and a palette that cannot
// make a page is not the palette the spec describes. This is the data layer, not another
// feature's UI — and duplicating the optimistic tree write would be far worse than the
// import: two writers of one cache entry is a bug waiting to happen.
import { appendPosition } from '@/features/notebook/treeModel';
import { useCreatePage, useTree, useUpdatePage } from '@/features/notebook/usePageMutations';
// M5. The palette is the app's primary way IN to the calendar as well: `New event` and
// `Set due date` open the two dialogs the shell hosts, and the `Go to` rows are the routes
// that did not exist in M4 — which is exactly why they were absent rather than disabled.
import { lastView } from '@/features/calendar/lastView';
import { openNewEvent } from '@/features/calendar/eventDialogStore';
import { openDueDate } from '@/features/calendar/dueDateStore';
import { formatTime, todayKey } from '@/features/calendar/datetime';
import { useCalendarSettings } from '@/features/calendar/useCalendarSettings';
// M7. `070` §4's command table is finally complete: Go to Settings, Go to Trash, Empty trash,
// and the cheatsheet — each added WITH the milestone that made its target exist.
import { EmptyTrashDialog } from '@/features/notebook/EmptyTrashDialog';
import { useTrash } from '@/features/notebook/usePageMutations';
import { ShortcutsDialog } from '@/keyboard/ShortcutsDialog';
import { editorHasTextSelection } from '@/features/editor/link';
import { useTranslation } from '@/lib/i18n';

/** The empty-query list (`070` §4): the 10 most recently updated pages. */
const RECENT_LIMIT = 10;

const NEXT_THEME: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };

function breadcrumbOf(titles: readonly string[]): string {
  // Truncates from the LEFT — the NEAREST ancestor is the informative one (`070` §4).
  return titles.length > 2 ? `… / ${titles.slice(-2).join(' / ')}` : titles.join(' / ');
}

function PageIcon({ icon }: { icon: string | null }) {
  if (icon === null) return <FileText strokeWidth={1.75} />;
  return (
    <span aria-hidden className="size-[var(--icon)] text-center leading-none">
      {icon}
    </span>
  );
}

export function CommandPalette() {
  const { t, plural } = useTranslation();
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  /** cmdk's selected row. Controlled ONLY so a mode switch can reset it (criterion 6). */
  const [selected, setSelected] = useState('');

  const [shortcuts, setShortcuts] = useState(false);
  const [emptying, setEmptying] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useThemePreference();
  const { toggleDensity } = useDensityPreference();

  // `useMatch`, not `useParams`: this component is mounted by the shell, which is an
  // ANCESTOR of `/p/:pageId`, and `useParams` there sees no params of its descendants.
  const pageMatch = useMatch('/p/:pageId');
  const currentPageId = pageMatch?.params.pageId;

  const tree = useTree();
  const tags = useTags();
  // Only fetched once the user actually asks to empty the trash — the archived tree is a second
  // full query and most sessions never look at it (`060` §5).
  const trash = useTrash(emptying);
  const { timezone } = useCalendarSettings();
  const { mutate: createPage } = useCreatePage();
  const { mutate: updatePage } = useUpdatePage();

  const { mode, query } = parsePaletteInput(raw);

  const pages: readonly TreePage[] = useMemo(() => tree.data?.pages ?? [], [tree.data]);
  const currentPage = useMemo(
    () => pages.find((page) => page.id === currentPageId),
    [pages, currentPageId],
  );

  /* ── Open / close ────────────────────────────────────────────────────────── */

  /**
   * Where focus was when the palette opened, so that dismissing it puts the user back.
   *
   * This file's header has always claimed *"Focus returns to whatever had it when the palette
   * opened — including the caret's position in the Tiptap document. Radix's `FocusScope` does
   * that for us."* Tabbing the app for M7's criterion 9 showed it does **not**: `Esc` left
   * `document.activeElement` on `<body>`, which dumps a keyboard user back to the top of the tab
   * order from wherever they were. A promise in a comment is not a feature; this is the feature.
   *
   * Only the DISMISS path restores. Every action in the palette navigates, and `run()` closes it
   * by calling `setOpen(false)` directly rather than through `onOpenChange` — so choosing a page
   * cannot yank focus back to the control you opened the palette from, which would be exactly
   * wrong.
   */
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const show = useCallback((prefix = '') => {
    const active = document.activeElement;
    restoreFocusTo.current = active instanceof HTMLElement ? active : null;
    setRaw(prefix);
    setSelected('');
    setOpen(true);
  }, []);

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) return;

    const target = restoreFocusTo.current;
    restoreFocusTo.current = null;
    if (target === null || !target.isConnected) return;

    // After Radix's own unmount-focus pass, not during it — otherwise it lands last and wins.
    // Focusing the ProseMirror element restores the caret too: the editor keeps its selection.
    requestAnimationFrame(() => {
      target.focus({ preventScroll: true });
    });
  }, []);

  const hotkeys: Hotkey[] = useMemo(
    () => [
      // Registered on `window` with `preventDefault`, so it works from ANYWHERE — including
      // inside the Tiptap editor, which must not swallow it (`070` §4). Firefox's Ctrl+K
      // (focus the search bar) is prevented along with it (`110` §7).
      //
      // THE ONE OVERLOAD (`110` §7): with a text selection in the document, `Mod+K` inserts a
      // link instead. Tiptap's binding takes it in that case and this one declines — the two
      // listeners live on different layers (the editable element vs `window`) and a
      // `preventDefault` in one does not stop the other, so both must ask the same question.
      {
        key: 'k',
        mod: true,
        handler: () => {
          if (editorHasTextSelection()) return;
          show();
        },
      },
      // `110` §7: "Open the palette in page-jump mode. Same palette, pre-filtered."
      {
        key: 'p',
        mod: true,
        handler: () => {
          show('@');
        },
      },
      {
        key: 'f',
        mod: true,
        shift: true,
        handler: () => {
          show();
        },
      },
    ],
    [show],
  );
  useHotkeys(hotkeys);

  // "Also closes on route change" (`070` §4). Every action below navigates, so this is what
  // actually dismisses the palette — nothing needs to remember to close it.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Switching prefix mid-query RESETS the selection to the first item (criterion 6). cmdk
  // selects the first item when the controlled value matches no row, so clearing it is the
  // whole implementation.
  useEffect(() => {
    setSelected('');
  }, [mode]);

  const run = useCallback((action: () => void) => {
    action();
    setOpen(false);
  }, []);

  /* ── Search ──────────────────────────────────────────────────────────────── */

  const results = useSearch(query, { enabled: open && (mode === 'search' || mode === 'pages') });
  const hits = results.data?.pages ?? [];
  // `@` is PAGES (see `paletteMode.ts` for the recorded `070` §4 / roadmap conflict — it is
  // deliberately NOT flipped here). Events therefore only appear in the bare-search mode,
  // grouped below the pages.
  const events = mode === 'search' ? (results.data?.events ?? []) : [];

  /**
   * The empty query. Favorites pinned above the 10 most recently updated pages — both from
   * the tree that is already in the cache. **No extra fetch** (`070` §4, §8).
   */
  const recent = useMemo(() => {
    const favorites = pages
      .filter((page) => page.isFavorite)
      .sort((a, b) => a.title.localeCompare(b.title));

    const favoriteIds = new Set(favorites.map((page) => page.id));
    const rest = [...pages]
      .filter((page) => !favoriteIds.has(page.id))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, RECENT_LIMIT);

    return { favorites, rest };
  }, [pages]);

  const matchingTags = useMemo(() => {
    const all = tags.data?.tags ?? [];
    if (query === '') return all;
    const needle = query.toLowerCase();
    return all.filter((tag) => tag.name.toLowerCase().includes(needle));
  }, [tags.data, query]);

  /* ── Actions ─────────────────────────────────────────────────────────────── */

  const openPage = (id: string): void => {
    run(() => {
      void navigate(`/p/${id}`);
    });
  };

  /**
   * "Create page 'foo'" — the no-results action row. A dead end is a wasted opportunity
   * (`070` §4).
   *
   * Note the ASYMMETRY with the `[[` menu, and that it is deliberate: here the user asked
   * for a page by name and found none, so making it is the obvious next move. There, the
   * user is writing a sentence, and auto-creating from typing would fill the notebook with
   * empty stubs (§7.1) — so the link is left unresolved instead.
   */
  const createNamedPage = (title: string, parentId: string | null): void => {
    const id = ulid();
    const position =
      parentId === null ? appendPosition(pages, null) : appendPosition(pages, parentId);

    createPage({ id, parentId, position, title });
    run(() => {
      void navigate(`/p/${id}`, { state: { focusEditor: true } });
    });
  };

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const placeholder =
    mode === 'commands'
      ? t('search.palette.placeholderCommands')
      : mode === 'tags'
        ? t('search.palette.placeholderTags')
        : t('search.palette.placeholder');

  return (
    <>
      {/* Both are hosted here rather than opened by navigation: the palette is the only place
          that can reach them, and a dialog opened FROM a dialog must outlive its opener. */}
      <ShortcutsDialog open={shortcuts} onOpenChange={setShortcuts} />
      <EmptyTrashDialog
        open={emptying}
        onOpenChange={setEmptying}
        pages={trash.data?.pages ?? []}
      />

      <CommandDialog open={open} onOpenChange={onOpenChange} label={t('search.palette.label')}>
        <Command
          // The server already ranked these across three legs (`070` §3). Re-filtering them
          // with cmdk's fuzzy matcher would throw away the trigram hits — the very rows that
          // make `ithakr` find `Ithkar` (criterion 2). Commands and tags ARE filtered, but by
          // us, above, not by cmdk.
          shouldFilter={false}
          value={selected}
          onValueChange={setSelected}
          loop
        >
          <CommandInput
            value={raw}
            onValueChange={setRaw}
            placeholder={placeholder}
            data-testid="palette-input"
          />

          <CommandList
            // "Never blank out the list while a new query is in flight — keep the previous
            // results and DIM them" (`070` §4).
            className={cn(results.isFetching && 'opacity-60 transition-opacity')}
            data-testid="palette-list"
          >
            {/* ── > commands (client-side, no network) ───────────────────────── */}
            {mode === 'commands' && (
              <CommandsList
                query={query}
                hasPage={currentPage !== undefined}
                isFavorite={currentPage?.isFavorite ?? false}
                theme={theme}
                onNewEvent={() => {
                  run(() => {
                    // Today, all-day — the same default the agenda's `+` uses. One click to a
                    // focused title field is the bar (`080` §9).
                    openNewEvent({ dayKey: todayKey(timezone), allDay: true });
                  });
                }}
                onSetDueDate={() => {
                  if (currentPageId === undefined) return;
                  run(() => {
                    openDueDate(currentPageId);
                  });
                }}
                onGoToAgenda={() => {
                  run(() => {
                    void navigate('/calendar');
                  });
                }}
                onGoToCalendar={() => {
                  run(() => {
                    void navigate(`/calendar/${lastView()}`);
                  });
                }}
                // M6. The route now exists, so the row can. (`Mod+E` opens the slide-over over
                // the page you are on; this goes to the full workspace.)
                onGoToChat={() => {
                  run(() => {
                    void navigate('/chat');
                  });
                }}
                onNewPage={() => {
                  createNamedPage('Untitled', null);
                }}
                onNewChild={() => {
                  if (currentPageId === undefined) return;
                  createNamedPage('Untitled', currentPageId);
                }}
                onToggleFavorite={() => {
                  if (currentPage === undefined) return;
                  run(() => {
                    updatePage({ id: currentPage.id, isFavorite: !currentPage.isFavorite });
                  });
                }}
                onToggleTheme={() => {
                  run(() => {
                    setTheme(NEXT_THEME[theme]);
                  });
                }}
                onToggleDensity={() => {
                  run(() => {
                    toggleDensity();
                  });
                }}
                onGoToSettings={() => {
                  run(() => {
                    void navigate('/settings');
                  });
                }}
                onGoToTrash={() => {
                  run(() => {
                    void navigate('/trash');
                  });
                }}
                onEmptyTrash={() => {
                  run(() => {
                    setEmptying(true);
                  });
                }}
                onShortcuts={() => {
                  run(() => {
                    setShortcuts(true);
                  });
                }}
              />
            )}

            {/* ── # tags ──────────────────────────────────────────────────────── */}
            {mode === 'tags' && (
              <>
                {matchingTags.length === 0 && (
                  <CommandEmpty>
                    {query === ''
                      ? t('search.palette.noTagsYet')
                      : t('search.palette.noTagsMatch', { query })}
                  </CommandEmpty>
                )}
                <CommandGroup heading={t('search.palette.tags')}>
                  {matchingTags.map((tag) => (
                    <CommandItem
                      key={tag.id}
                      value={`tag:${tag.id}`}
                      onSelect={() => {
                        run(() => {
                          void navigate(`/tags/${tag.id}`);
                        });
                      }}
                    >
                      <TagIcon
                        strokeWidth={1.75}
                        style={tag.color === null ? undefined : { color: tag.color }}
                      />
                      <span className="truncate">{tag.name}</span>
                      <CommandShortcut>
                        {plural('search.palette.pageCount', tag.pageCount)}
                      </CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {/* ── search / @pages ─────────────────────────────────────────────── */}
            {(mode === 'search' || mode === 'pages') && (
              <>
                {query === '' ? (
                  <>
                    {recent.favorites.length > 0 && (
                      <CommandGroup heading={t('search.palette.favorites')}>
                        {recent.favorites.map((page) => (
                          <CommandItem
                            key={page.id}
                            value={`fav:${page.id}`}
                            onSelect={() => {
                              openPage(page.id);
                            }}
                          >
                            <Star strokeWidth={1.75} className="text-[var(--warning)]" />
                            <span className="truncate">{page.title}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    <CommandGroup heading={t('search.palette.recent')}>
                      {recent.rest.map((page) => (
                        <CommandItem
                          key={page.id}
                          value={`recent:${page.id}`}
                          onSelect={() => {
                            openPage(page.id);
                          }}
                        >
                          <PageIcon icon={page.icon} />
                          <span className="truncate">{page.title}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                ) : (
                  <>
                    {hits.length === 0 && !results.isFetching && (
                      <CommandEmpty>{t('search.palette.noPagesMatch', { query })}</CommandEmpty>
                    )}

                    {hits.length > 0 && (
                      <CommandGroup heading={t('search.palette.pages')}>
                        {hits.map((hit) => (
                          <CommandItem
                            key={hit.id}
                            value={`page:${hit.id}`}
                            onSelect={() => {
                              openPage(hit.id);
                            }}
                            // Two lines when there is a snippet, so the row must not be a
                            // fixed --row-h.
                            className="h-auto min-h-[var(--row-h)] items-start py-1"
                          >
                            <span className="mt-0.5 flex size-[var(--icon)] shrink-0 items-center justify-center">
                              <PageIcon icon={hit.icon} />
                            </span>

                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="flex items-baseline gap-2">
                                <span className="truncate">{hit.title}</span>
                                {hit.breadcrumb.length > 0 && (
                                  <CommandShortcut className="truncate">
                                    {breadcrumbOf(hit.breadcrumb.map((crumb) => crumb.title))}
                                  </CommandShortcut>
                                )}
                              </span>

                              {/* Server HTML. Parsed into React elements — NEVER
                                dangerouslySetInnerHTML (`070` §5, criterion 7). */}
                              {hit.snippet !== null && hit.snippet !== '' && (
                                <SearchSnippet html={hit.snippet} />
                              )}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    {/* Events — a secondary result class, capped at 5 server-side (`070` §1.5),
                      in their own group BELOW pages (`070` §4). The M4 stub filled `events`
                      with nothing; M5's backend fills it, and this renders it. Selecting one
                      opens the agenda at that event's week — the calendar's own screens are
                      where an event is edited, not the palette. */}
                    {events.length > 0 && (
                      <CommandGroup heading={t('search.palette.events')}>
                        {events.map((hit) => (
                          <CommandItem
                            key={hit.id}
                            value={`event:${hit.id}`}
                            onSelect={() => {
                              run(() => {
                                void navigate(`/calendar/week?date=${hit.startsAt.slice(0, 10)}`);
                              });
                            }}
                          >
                            <CalendarDays strokeWidth={1.75} />
                            <span className="truncate">{hit.title}</span>
                            <CommandShortcut>
                              {hit.startsAt.slice(0, 10)} · {formatTime(hit.startsAt, timezone)}
                            </CommandShortcut>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    {/* A dead end is a wasted opportunity (`070` §4). */}
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        value="create-page"
                        onSelect={() => {
                          createNamedPage(query, null);
                        }}
                      >
                        <FilePlus2 strokeWidth={1.75} />
                        <span className="truncate">
                          {t('search.palette.createPagePrefix')}
                          <span className="font-medium">{query}</span>
                          {t('search.palette.createPageSuffix')}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}

/* ── The `>` command list (`070` §4, "v1, complete") ───────────────────────────
 *
 * Contextual commands are **hidden, not disabled**, when unavailable: "a greyed-out row you
 * cannot use is noise" (`070` §4).
 *
 * The rows below are the ones the app can actually perform. **M5 adds four** — New event, Set
 * due date, Go to Agenda, Go to Calendar — because M5 is when their routes and dialogs came to
 * exist; they were absent, not disabled, precisely so that no row here has ever done nothing.
 * **M6 adds Go to AI Chat**, for the same reason: `/chat` did not exist until now. The
 * remainder of `070` §4's table (Go to Settings, Empty trash) still needs M7. Add each one WITH
 * its milestone.
 */
function CommandsList({
  query,
  hasPage,
  isFavorite,
  theme,
  onNewEvent,
  onSetDueDate,
  onGoToAgenda,
  onGoToCalendar,
  onGoToChat,
  onNewPage,
  onNewChild,
  onToggleFavorite,
  onToggleTheme,
  onToggleDensity,
  onGoToSettings,
  onGoToTrash,
  onEmptyTrash,
  onShortcuts,
}: {
  query: string;
  hasPage: boolean;
  isFavorite: boolean;
  theme: Theme;
  onNewEvent: () => void;
  onSetDueDate: () => void;
  onGoToAgenda: () => void;
  onGoToCalendar: () => void;
  onGoToChat: () => void;
  onNewPage: () => void;
  onNewChild: () => void;
  onToggleFavorite: () => void;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
  onGoToSettings: () => void;
  onGoToTrash: () => void;
  onEmptyTrash: () => void;
  onShortcuts: () => void;
}) {
  const { t } = useTranslation();

  const commands = [
    {
      id: 'new-page',
      label: t('search.commands.newPage'),
      icon: <FilePlus2 strokeWidth={1.75} />,
      hint: 'Mod+N',
      run: onNewPage,
      show: true,
    },
    {
      id: 'new-event',
      label: t('search.commands.newEvent'),
      icon: <CalendarPlus strokeWidth={1.75} />,
      hint: undefined,
      run: onNewEvent,
      show: true,
    },
    {
      // Contextual: hidden, not greyed out, when there is no page to hang a deadline on.
      id: 'set-due-date',
      label: t('search.commands.setDueDate'),
      icon: <CalendarClock strokeWidth={1.75} />,
      hint: undefined,
      run: onSetDueDate,
      show: hasPage,
    },
    {
      id: 'go-agenda',
      label: t('search.commands.goToAgenda'),
      icon: <ListTodo strokeWidth={1.75} />,
      hint: undefined,
      run: onGoToAgenda,
      show: true,
    },
    {
      id: 'go-calendar',
      label: t('search.commands.goToCalendar'),
      icon: <CalendarDays strokeWidth={1.75} />,
      hint: undefined,
      run: onGoToCalendar,
      show: true,
    },
    {
      id: 'go-chat',
      label: t('search.commands.goToChat'),
      icon: <Sparkles strokeWidth={1.75} />,
      hint: 'Mod+E',
      run: onGoToChat,
      show: true,
    },
    {
      id: 'new-child',
      label: t('search.commands.newChildPage'),
      icon: <FilePlus2 strokeWidth={1.75} />,
      hint: 'Mod+Shift+N',
      run: onNewChild,
      show: hasPage,
    },
    {
      id: 'toggle-favorite',
      label: isFavorite
        ? t('search.commands.removeFromFavorites')
        : t('search.commands.addToFavorites'),
      icon: isFavorite ? <StarOff strokeWidth={1.75} /> : <Star strokeWidth={1.75} />,
      hint: undefined,
      run: onToggleFavorite,
      show: hasPage,
    },
    {
      id: 'toggle-theme',
      label: t('search.commands.toggleTheme', { theme }),
      icon: <Moon strokeWidth={1.75} />,
      hint: undefined,
      run: onToggleTheme,
      show: true,
    },
    {
      id: 'toggle-density',
      label: t('search.commands.toggleDensity'),
      icon: <Rows3 strokeWidth={1.75} />,
      hint: undefined,
      run: onToggleDensity,
      show: true,
    },
    {
      id: 'go-settings',
      label: t('search.palette.openSettings'),
      icon: <SettingsIcon strokeWidth={1.75} />,
      hint: 'G then S',
      run: onGoToSettings,
      show: true,
    },
    {
      id: 'go-trash',
      label: t('search.commands.openTrash'),
      icon: <Trash2 strokeWidth={1.75} />,
      hint: 'G then T',
      run: onGoToTrash,
      show: true,
    },
    {
      id: 'empty-trash',
      label: t('search.commands.emptyTrash'),
      icon: <Trash2 strokeWidth={1.75} />,
      hint: undefined,
      run: onEmptyTrash,
      show: true,
    },
    {
      id: 'shortcuts',
      label: t('search.commands.shortcuts'),
      icon: <Keyboard strokeWidth={1.75} />,
      hint: '?',
      run: onShortcuts,
      show: true,
    },
  ].filter((command) => command.show);

  // Fuzzy-ish, and client-side: a substring match on the label. cmdk's own filter is off for
  // the whole palette (the server ranks the search legs), so the `>` list filters itself.
  const needle = query.toLowerCase();
  const visible = commands.filter((command) => command.label.toLowerCase().includes(needle));

  if (visible.length === 0) {
    return <CommandEmpty>{t('search.palette.noCommandsMatch', { query })}</CommandEmpty>;
  }

  return (
    <CommandGroup heading={t('search.commands.heading')}>
      {visible.map((command) => (
        <CommandItem key={command.id} value={command.id} onSelect={command.run}>
          {command.icon}
          <span className="truncate">{command.label}</span>
          {command.hint !== undefined && <CommandShortcut>{command.hint}</CommandShortcut>}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
